<template>
  <b-modal :id="id" size="xl" @hidden="resetModal" hide-header hide-footer body-class="m-0 p-0">
    <b-overlay :show="isLoadingCurrent.recipient">

      <div class="p-3 rounded">
        <div class="d-flex flex-column">
          <h5 class="m-0">
            <b>{{ $t('ordemResultado.modal.table.recipiente') }}</b>: {{ identifier }}
          </h5>
          <h5 class="m-0">
            <b>{{ $t('ordemResultado.modal.table.paciente') }}</b>: {{ patient }}
          </h5>
          <h5 class="m-0" v-if="athleteId">
            <b>{{ $t('ordemResultado.modal.table.athlete') }}</b>: {{ athleteId }}
          </h5>
          <i class="fas fa-times"
             style="position:absolute; top:10px; right:10px; cursor:pointer;"
             @click="$bvModal.hide(id)"
          />
        </div>

        <hr/>

        <div class="row font-weight-bold">
          <div class="col-2 p-3">{{ $t('ordemResultado.modal.recipiente.exames') }}</div>
          <div class="col-4 p-3">{{ $t('ordemResultado.modal.recipiente.horario') }}</div>
          <div class="col-1 p-3">{{ $t('ordemResultado.modal.recipiente.arquivos') }}</div>
          <div class="col-2 p-3">{{ $t('ordemResultado.modal.recipiente.status') }}</div>
          <div class="col-3 p-3">{{ $t('ordemResultado.modal.recipiente.acoes') }}</div>
        </div>
        <b-card v-for="transaction in transactions" :key="transaction.recipientId + '-' + transaction.type + '-' + transaction.id" class="mb-2 shadow" body-class="p-2">
          <div class="row d-flex align-items-center">
            <div class="col-2">
              <b-btn variant="light mr-2" size="sm" pill @click="loadDetails('ExamsDetail', transaction)">
                <i class="fas fa-chevron-circle-down"/>
              </b-btn>
            </div>
            <div class="col-4">
              <p class="mb-0">
                <b v-if="transaction.type === 'Report'">
                  {{ $t('ordemResultado.modal.recipiente.resultado') }}
                </b>
                <b v-else>
                  {{ $t('ordemResultado.modal.recipiente.ordem') }}
                </b>
              </p>
              {{ getDate(transaction) | formatDate }}
            </div>
            <div class="col-1">
              <b-btn variant="link ml-3" size="sm" pill @click="loadDetails('FilesDetail', transaction)">
                <i class="fas fa-file-code"/>
              </b-btn>
            </div>
            <div class="col-2">
              <span :class="{ 'text-danger': transaction.status === 'cancelled' }">
                {{ transaction.status }}
              </span>
            </div>
            <div class="col-3 d-flex">
              <b-btn v-if="!(transaction.type === 'Report')"
                     variant="outline-primary mr-2"
                     size="sm"
                     @click="loadDetails('ReportResultDetail', transaction)"
                     pill>
                <i class="fas fa-registered"/>
              </b-btn>
              <b-overlay :show="printing">
                <div v-if="transaction.exams && transaction.exams[0].support && transaction.exams[0].support.sup_request_number"
                     class="d-inline">
                  <b-button v-if="!(transaction.type === 'Report')"
                            @click="rePrint(transaction)"
                            variant="outline-warning mr-2"
                            size="sm"
                            :title="$t('ordemResultado.modal.reemprimir_etiqueta')"
                            v-b-tooltip
                            pill>
                    <i class="fas fa-print"/>
                  </b-button>
                  <b-button @click="getObsersationFromSupport()"
                            variant="outline-primary mr-2"
                            size="sm"
                            :title="$t('ordemResultado.modal.getObservation.pending')"
                            v-b-tooltip
                            pill>
                    <i class="fas fa-sync"/>
                  </b-button>
                  <b-button @click="getObsersationFromSupport('all')"
                            variant="outline-primary"
                            size="sm"
                            :title="$t('ordemResultado.modal.getObservation.all')"
                            v-b-tooltip
                            pill>
                    <i class="fas fa-sync"/>
                  </b-button>
                </div>
              </b-overlay>
            </div>
          </div>
          <div v-show="detailToShow[transaction.index] && detailToShow[transaction.index].showing" class="mt-2">            
            <ExamsDetail
                v-if="detailToShow[transaction.index] && detailToShow[transaction.index].component === 'ExamsDetail'"
                :type-transaction="transaction.type"
                :id="transaction.id"
                :transaction="transaction"
                @search="$emit('search')"
            />
            <FilesDetail
                v-if="detailToShow[transaction.index] && detailToShow[transaction.index].component === 'FilesDetail'"
                :type-transaction="transaction.type"
                :id="transaction.id"
            />
            <ReportResultDetail
                v-show="detailToShow[transaction.index] && detailToShow[transaction.index].component === 'ReportResultDetail'"
                :identifier="identifier"
                :order="transaction"
            />
          </div>
        </b-card>

        <Loading v-show="isLoadingCurrent.orders">
          {{ $t('ordemResultado.modal.recipiente.carregando_ordens') }}
        </Loading>
        <Loading v-show="isLoadingCurrent.reports">
          {{ $t('ordemResultado.modal.recipiente.carregando_resultados') }}
        </Loading>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import OrderRepository from "@/repositories/vns/OrderRepository";
import {mapGetters, mapActions} from 'vuex';
import ExamsDetail from "@/pages/vns/observation/ExamsDetail";
import FilesDetail from "@/pages/vns/observation/FilesDetail";
import ReportResultDetail from "@/pages/vns/observation/ReportResultDetail";
import Loading from "@/components/Loading";

export default {
  name: "RecipientModal",
  components: {Loading, ReportResultDetail, FilesDetail, ExamsDetail},
  props: {
    id: {
      type: String,
      default: 'modal-observation'
    },
    title: {
      type: String,
      default: ''
    }
  },
  computed: {
    ...mapGetters({
      recipient: 'recipient/current',
      isLoadingCurrent: 'recipient/isLoadingCurrent',
    }),
    identifier() {
      if (this.recipient instanceof Array) {
        return this.recipient[0].identifier
      }
      if (!this.recipient instanceof Array) {
        return this.recipient.identifier
      }
      return null
    },
    athleteId() {
      if (this.recipient instanceof Array) {        
        if (this.recipient[0].orders instanceof Array) {
          return (this.recipient[0].orders[0] !== undefined) ?  this.recipient[0].orders[0].athlete_id : null ;
        }else{          
          return (this.recipient[0].orders !== undefined) ?  this.recipient[0].orders.athlete_id : null ;
        }
      }
      if (!this.recipient instanceof Array) {
        if (this.recipient.orders instanceof Array) {          
          return (this.recipient.orders[0] !== undefined) ?  this.recipient.orders[0].athlete_id : null ;
        }else{
          return this.recipient.orders.athlete_id
          return (this.recipient.orders !== undefined) ?  this.recipient.orders.athlete_id : null ;
        }
      }
      return null
    },
    transactions() {
      let transactions = []
      if (this.recipient instanceof Array) {
        this.recipient.map(r => {
          const orders = r.orders.map(obj=> {return {...obj, type: 'Order', recipientId: r.id }})
          const reports = r.reports.map(obj=> {return {...obj, type: 'Report', recipientId: r.id }})
          transactions = [...transactions, ...orders, ...reports]
        })
      }
      if (!this.recipient instanceof Array) {
        const orders = this.recipient.orders.map(obj=> {return {...obj, type: 'Order', recipientId: this.recipient.id }})
        const reports = this.recipient.reports.map(obj=> {return {...obj, type: 'Report', recipientId: this.recipient.id }})
        transactions = [...orders, ...reports]
      }

      transactions.sort((a, b) => {
        if (a.created_at < b.created_at) {
          return 1;
        }
        if (a.created_at > b.created_at) {
          return -1;
        }
        return 0;
      })

      return transactions.map((t, idx) => {
        if (!this.detailToShow[idx]) {
          this.detailToShow[idx] = {showing: false, component: null}
        }

        return {...t, index: idx}
      })
    },
    patient() {
      let patientArray = []
      this.transactions.map(e => {
        if (e.patient && !patientArray.includes(e.patient))
          patientArray = [...patientArray, e.patient]
      })

      let patientString = patientArray.shift()
      if (patientArray.length > 0) {
        patientArray.map(p => {
          patientString += ` | ${p}`
        })
      }
      return patientString
    },
  },
  data() {
    return {
      printing: false,
      detailToShow: [],
      hasAutoExpanded: false,
      ordersLoaded: false,
      reportsLoaded: false,
    }
  },
  watch: {
    'isLoadingCurrent.orders'(loading, wasLoading) {
      if (wasLoading && !loading) {
        this.ordersLoaded = true
      }
    },
    'isLoadingCurrent.reports'(loading, wasLoading) {
      if (wasLoading && !loading) {
        this.reportsLoaded = true
      }
    },
    ordersLoaded() {
      this.autoExpandMostRecent()
    },
    reportsLoaded() {
      this.autoExpandMostRecent()
    }
  },
  methods: {
    ...mapActions([
      'recipient/listOne',
      'recipient/listOrders',
      'recipient/listReports'
    ]),
    resetModal() {
      this.printing = false
      this.detailToShow = []
      this.hasAutoExpanded = false
      this.ordersLoaded = false
      this.reportsLoaded = false
      this.$forceUpdate()
    },
    autoExpandMostRecent() {
      if (this.hasAutoExpanded || !this.ordersLoaded || !this.reportsLoaded || this.transactions.length === 0) {
        return
      }

      this.hasAutoExpanded = true
      this.$set(this.detailToShow, 0, {showing: true, component: 'ExamsDetail'})
    },
    loadTransactions () {
      if (this.recipient instanceof Array) {
        this.recipient.map(async r => {
          this['recipient/listOrders'](r.id)
          this['recipient/listReports'](r.id)
        })
      }

      if (!this.recipient instanceof Array){
        this['recipient/listOrders'](this.recipient.id)
        this['recipient/listReports'](this.recipient.id)
      }
    },
    async rePrint(item) {
      this.printing = true
      try {
        await OrderRepository.rePrint({
          vnsName: item.vns,
          recipientIdentifier: this.identifier
        }).then(() => {
          this.$snotify.success(this.$t('ordemResultado.messages.reprint.success'))
          this.loadTransactions()
        }).catch(() => {
          this.$snotify.error(this.$t('ordemResultado.messages.reprint.error'))
        })
      } catch (e) {
        //console.log('ERROR:', e)
      }
      this.printing = false
    },
    async getObsersationFromSupport(status) {
      const supportStatus = status && status === 'all' ? ['pending', 'awaiting', 'success'] : ['pending', 'awaiting'];
      let notificationSent = false;

      const resolve = () => {
        this['recipient/listOne']()
        if (!notificationSent) {
          notificationSent = true;
          this.$snotify.success(this.$t('ordemResultado.messages.getObservation.success'))
        }
      }
      const reject = () => {
        if (!notificationSent) {
          notificationSent = true;
          this.$snotify.error(this.$t('ordemResultado.messages.getObservation.error'))
        }
      }

      await supportStatus.map(async status => {
        await OrderRepository.getSupportObservations({
          status,
          identifier: this.identifier
        }).then(resolve)
            .catch(reject)
      })
    },
    loadDetails(component, transaction) {
      if (
          !this.detailToShow[transaction.index].showing ||
          this.detailToShow[transaction.index].showing && this.detailToShow[transaction.index].component !== component
      ) {
        this.detailToShow[transaction.index] = {
          showing: true,
          component
        }
      } else if (this.detailToShow[transaction.index].showing && this.detailToShow[transaction.index].component === component) {
        this.detailToShow[transaction.index] = {
          showing: false,
          component: null
        }
      }

      this.$forceUpdate()
    },
    getDate(item) {
      if (item.type === 'Report') {
        if (item.updated_at && (item.updated_at >= item.processed_at)) {
          return item.updated_at
        }
        if (item.processed_at && (item.processed_at >= item.created_at)) {
          return item.processed_at
        }
      }

      if (item.updated_at && (item.updated_at >= item.collected_at)) {
        return item.updated_at
      }
      if (item.collected_at && (item.collected_at >= item.received_at)) {
        return item.collected_at
      }
      if (item.received_at && (item.received_at >= item.created_at)) {
        return item.received_at
      }
      return item.created_at
    }
  }
}
</script>

<style lang="scss" scoped>
/deep/ .b-table-details {
  td {
    padding: 0.5rem !important;

    table {
      width: 80%;
      margin: auto;
    }
  }
}

/deep/ .row-item {
  padding-top: 5px;
  padding-bottom: 5px;
  background-color: white;
  margin-top: 2px;
  margin-bottom: 2px;
}
</style>